import { AssetPath } from '@gnsx/genesys.js';
import * as ENGINE from '@gnsx/genesys.js';
import { isEmpty } from 'es-toolkit/compat';
import { toast } from 'sonner';


import type { EditorObjectData } from '@/app/editor/core/editorClassData';

import { editorActions, nameHelpers, objectHelpers, sceneActions, sceneHandler } from '@/editor/core';
import { showAssetInAssetBrowser } from '@/editor/core/actions/assetBrowserReveal';
import { actorLogRef, editableObjectOrNodeRef } from '@/editor/core/engineLogRefs';
import {
  excludeActorByUuid,
  normalizePrefabAssetPath,
  prefabEditPathsMatch,
  resolvePrefabCreateDirectory,
} from '@/editor/core/prefabActionHelpers';
import { resolvePrefabUndoStackKey, syncActiveUndoStack } from '@/editor/core/undoRedo';
import { assetsStore, editorAssets, editorStore, sceneStore, store } from '@/editor/store';
import { type IEditableObject, type ISceneTreeNode } from '@/editor/store/models';

export type PrefabCloseRequest = {
  nextActorIdToSelect?: string | null;
};

let requestClosePrefabHandler: ((request?: PrefabCloseRequest) => void) | null = null;

/** Registered by {@link PrefabCloseProvider} for Esc / toolbar close with unsaved prompt. */
export function registerRequestClosePrefab(
  handler: ((request?: PrefabCloseRequest) => void) | null,
): void {
  requestClosePrefabHandler = handler;
}

/** Close prefab mode, prompting when the prefab asset is dirty (when provider is mounted). */
export function requestClosePrefab(request?: PrefabCloseRequest): void {
  if (requestClosePrefabHandler) {
    requestClosePrefabHandler(request);
    return;
  }
  closePrefab(request?.nextActorIdToSelect);
}

function resolvePrefabAssetFile(prefabPath: string) {
  const normalized = normalizePrefabAssetPath(prefabPath);
  if (!normalized) {
    return undefined;
  }
  return assetsStore.assetTree.findFile(normalized)
    ?? assetsStore.assetTree.findFile(prefabPath);
}

/** Whether the prefab currently being edited has unsaved changes. */
export function isPrefabEditDirty(): boolean {
  if (!sceneStore.prefabEditPath) {
    return false;
  }
  if (sceneStore.prefabEditDirty) {
    return true;
  }
  const path = sceneStore.prefabEditPath;
  return resolvePrefabAssetFile(path)?.dirty ?? false;
}

/** Mark the prefab asset dirty without writing to disk. */
export function markPrefabDirty(prefabPath?: string): void {
  const path = prefabPath ?? sceneStore.prefabEditPath;
  if (!path) {
    return;
  }
  sceneStore.setPrefabEditDirty(true);
  resolvePrefabAssetFile(path)?.setDirty(true, { updateThumbnail: false, updatePreview: false });
}

/** Clears prefab editor dirty state after save or discard. */
export function clearPrefabEditDirty(prefabPath?: string): void {
  sceneStore.setPrefabEditDirty(false);
  const path = prefabPath ?? sceneStore.prefabEditPath;
  if (!path) {
    return;
  }
  resolvePrefabAssetFile(path)?.setDirty(false);
}

function getPrefabPathLabel(prefabPath: string): string {
  const withoutExt = prefabPath.endsWith(ENGINE.PREFAB_EXTENSION)
    ? prefabPath.slice(0, -ENGINE.PREFAB_EXTENSION.length)
    : prefabPath;
  const parts = withoutExt.split(/[/\\]/);
  return parts.pop() ?? withoutExt;
}

function syncPrefabActorNameWithAssetPath(actor: ENGINE.Actor, prefabPath: string): boolean {
  const expectedName = getPrefabPathLabel(prefabPath);
  if (!expectedName || actor.name === expectedName) {
    return false;
  }
  actor.setName(expectedName);
  return true;
}

function syncActorNameInEditorTrees(actor: ENGINE.Actor): void {
  const actorName = actor.name;
  if (!actorName) {
    return;
  }
  const outlinerNode = sceneStore.outliner.getNodeById(actor.uuid);
  if (outlinerNode) {
    outlinerNode.setName(actorName);
    outlinerNode.init(actor);
  }
  const selectedObject = sceneStore.getSelectedObjectById(actor.uuid);
  if (selectedObject) {
    selectedObject.setName(actorName);
  }
}

const prefabNamesPendingNameSyncSave = new Set<string>();

export async function autoSaveSceneAfterPrefabMutation(context: string): Promise<void> {
  if (!sceneStore.world || !editorStore.project.currentScene) {
    return;
  }

  try {
    await sceneHandler.saveScene();
  } catch (error) {
    console.error(`[PrefabActions:${context}] Failed to auto-save scene after prefab mutation`, error);
  }
}


/**
 * Fetches prefab names from path
 */
export async function fetchPrefabNames(path: string) {
  const storageProvider = store.project.makeScopedStorageProvider();
  const filelist = await storageProvider.listFiles(AssetPath.fromString(path));
  return filelist.files.filter(file => file.name.endsWith(ENGINE.PREFAB_EXTENSION)).map(file => file.name.replace(ENGINE.PREFAB_EXTENSION, ''));
}

/**
 * Creates a prefab from an actor class
 */
export async function createPrefabFromClass(name: string, path: string, actorClassName: string = 'ENGINE.Actor') {
  try {
    const actorClass = assetsStore.actorClasses.find(i => i.className === actorClassName);
    if (!actorClass) {
      console.warn('[PrefabActions:createPrefabFromClass] No actor class found for', actorClassName);
      return false;
    }
    const prefabActor = actorClass.createInstance();
    if (!prefabActor) {
      console.warn('[PrefabActions:createPrefabFromClass] Cannot create actor instance', actorClassName);
      return false;
    }
    await createPrefabActorAsset(name, path, prefabActor);
    return true;
  } catch (error) {
    console.error('[PrefabActions:createEmptyPrefab] Error creating prefab', name, path, actorClassName, error);
    return false;
  }
}

/**
 * Creates a prefab from a scene actor object.
 * @param object The editable object to create a prefab from.
 * @param path The target directory for the prefab.
 * @param preferredName Optional explicit asset name from save-as dialog.
 */
export async function createPrefabFromObject(
  object: IEditableObject | ISceneTreeNode,
  path: string,
  preferredName?: string,
) {
  const actor = object?.asActor;
  if (actor) {
    const desiredName = preferredName?.trim() || object.name;
    await createPrefabActorAsset(desiredName, path, actor);

  } else {
    toast.error(`Failed to create prefab: ${object.name}`);
  }
};

/**
 * Creates a prefab from an actor (the actor could be in the world or not)
 */
async function createPrefabActorAsset(desiredName: string, desiredPath: string, actor: ENGINE.Actor) {
  if (!actor) {
    console.warn('[PrefabActions:createPrefabActorAsset] No actor found to create prefab', desiredName, desiredPath);
    return;
  }

  if (!isEmpty(actor.getPrefabName())) {
    console.warn('[PrefabActions:createPrefabActorAsset] Already a prefab', desiredName, desiredPath);
    return;
  }

  const storageProvider = store.project.makeScopedStorageProvider();
  try {
    const targetDirectory = resolvePrefabCreateDirectory(desiredPath, assetsStore.currentPrefabDirectory);
    const filelist = await storageProvider.listFiles(AssetPath.fromString(targetDirectory));
    const fileExt = ENGINE.PREFAB_EXTENSION;

    // Ensure unique name, case insensitive
    const existingNames = filelist.files.filter(file => file.name.endsWith(fileExt)).map(file => file.name.replace(fileExt, ''));
    const newName = nameHelpers.makeUniqueName(desiredName, existingNames);

    // Set prefab path
    const prefabData = exportPrefabData(actor);
    const prefabName = nameHelpers.joinPaths(targetDirectory, newName) + fileExt;
    const prefabJson = JSON.stringify(prefabData, null, 2);

    // Save prefab asset
    const result = await storageProvider.uploadFile(AssetPath.fromString(prefabName), new Blob([prefabJson], { type: 'application/json' }));

    ENGINE.PrefabManager.setPrefabData(prefabName, undefined);
    ENGINE.resourceManager.invalidateJsonObjectCache(prefabName);
    assetsStore.updatePrefabData(prefabName, prefabData);
    editorActions.notifyChanged('CreateNewPrefab', { name: newName, path: prefabName });
    toast.success('Prefab asset created', { description: `Name: ${newName}, Path: ${prefabName}` });

    // Set prefab name and save scene.
    ENGINE.setAsPrefabInstance(actor, prefabName);
    syncActorNameInEditorTrees(actor);
    await autoSaveSceneAfterPrefabMutation('createPrefabActorAsset');

    // Show in asset browser
    await assetsStore.assetTree.refreshDirectory(targetDirectory);
    showAssetInAssetBrowser(prefabName, { selectIfFound: true });

    // Refresh thumbnail
    const thumbnailService = editorStore.thumbnailService;
    if (thumbnailService) {
      const assetFile = assetsStore.assetTree.files.get(prefabName);
      if (assetFile?.supportsThumbnails) {
        thumbnailService.queueThumbnailGeneration(assetFile, 100, true);
      }
    }

  } catch (error) {
    console.error('[PrefabActions:createPrefabActorAsset] Error creating prefab for', actorLogRef(actor), error);
  }
}

/**
 * Creates prefab data from an actor.
 */
export function exportPrefabData(actor: ENGINE.Actor, inlineToPrefab: boolean = false) {
  const prefabData = ENGINE.WorldSerializer.createPrefabDataFromActor(actor, inlineToPrefab);
  return prefabData;
}

function exportPrefabDataWithoutRootLocalTransform(
  actor: ENGINE.Actor,
  inlineToPrefab: boolean = false
): ENGINE.SerializedData {
  const prefabData = exportPrefabData(actor, inlineToPrefab);

  try {
    const prefabActor = ENGINE.WorldSerializer.loadActorSync(prefabData, {}, true);
    if (!prefabActor) {
      return prefabData;
    }
    prefabActor.stripRootLocalTransform();
    return ENGINE.WorldSerializer.createPrefabDataFromActor(prefabActor, inlineToPrefab);
  } catch (error) {
    console.warn('[PrefabActions] Failed to strip root local transform from prefab export', actorLogRef(actor), error);
    return prefabData;
  }
}

/**
 * Saves a prefab actor asset in prefab mode (non world scene).
 */
export async function savePrefabAsset(prefabActor: ENGINE.Actor, silent: boolean = false) {
  const storageProvider = store.project.makeScopedStorageProvider();
  let prefabName: string | undefined;
  try {
    prefabName = objectHelpers.getPrefabName(prefabActor);
    if (!prefabName) {
      toast.error(`Failed to save prefab: ${prefabName}`);
      console.error('[PrefabActions:savePrefabAssetActor] No prefab path found for', actorLogRef(prefabActor));
      return;
    }
    const instancesSnapshots = collectPrefabInstancesSnapshots(prefabName);

    const prefabData = exportPrefabData(prefabActor);

    // console.log('[PrefabActions:savePrefabAssetActor] Saving prefab asset', prefabName, prefabData);

    const prefabJson = JSON.stringify(prefabData, null, 2);
    const result = await storageProvider.uploadFile(AssetPath.fromString(prefabName), new Blob([prefabJson], { type: 'application/json' }));

    ENGINE.PrefabManager.setPrefabData(prefabName, undefined); // Clear prefab data cache to ensure next load gets the updated data
    ENGINE.resourceManager.invalidateJsonObjectCache(prefabName);

    assetsStore.updatePrefabData(prefabName, prefabData);

    editorActions.notifyChanged('savePrefabAssetActor', { path: result.path });
    if (!silent) {
      toast.success(`Prefab saved: ${prefabName}`);
    }
    reloadPrefabInstances(prefabName, instancesSnapshots);

    clearPrefabEditDirty(prefabName);

    // Request thumbnail verification and refresh if needed
    const thumbnailService = editorStore.thumbnailService;
    const assetFile = resolvePrefabAssetFile(prefabName);
    if (thumbnailService && assetFile?.supportsThumbnails) {
      thumbnailService.queueThumbnailGeneration(assetFile, 100, true);
    }

  } catch (error) {
    toast.error(`Failed to save prefab: ${prefabName}`);
    console.error('[PrefabActions:savePrefabAssetActor] Error saving prefab asset', actorLogRef(prefabActor), error);
  }
}

/**
 * Ensures a prefab asset is loaded and cached.
 * @param prefabName - The name of the prefab asset to ensure.
 * @param invalidateCache - Whether to invalidate the cache.
 * @returns True if the prefab asset is loaded and cached, false otherwise.
 */
/**
 * Reloads the in-memory prefab editor actor when disk content changes externally
 * (asset refresh, reimport, or `ensurePrefabAsset` with cache invalidation).
 */
export function handlePrefabAssetExternalReload(prefabPath: string): void {
  const editingPath = sceneStore.prefabEditPath;
  if (!editingPath || !prefabEditPathsMatch(editingPath, prefabPath)) {
    return;
  }

  console.log('[PrefabActions] External prefab reload — clearing prefab undo stack', prefabPath);
  editorStore.undoRedoManager.clearStack(resolvePrefabUndoStackKey(prefabPath));
  clearPrefabEditDirty(prefabPath);

  const actor = loadPrefabAsset(prefabPath);
  const editable = sceneStore.selectedObjects.find(obj => obj.isPrefabAsset) ?? sceneStore.selectedObjects[0];
  if (!actor || !editable) {
    return;
  }

  const selectedNodeIds = [...editable.hierarchy.selectedNodeIds];
  const expandedNodeIds = [...editable.hierarchy.expandedNodeIds];
  editable.init(actor, true);
  editable.hierarchy.setExpandedNodesIds(expandedNodeIds);
  const validSelectedIds = selectedNodeIds.filter(id => editable.hierarchy.getNodeById(id));
  if (validSelectedIds.length > 0) {
    editable.hierarchy.setSelectedNodeIds(validSelectedIds);
  } else {
    editable.hierarchy.selectNode(actor.uuid, false);
  }
  editable.bumpUpdateCounter();
  sceneStore.updateInspector();
}

export async function ensurePrefabAsset(prefabName: string, invalidateCache: boolean = false): Promise<boolean> {
  if (!invalidateCache && assetsStore.prefabMap.has(prefabName)) {
    return true;
  }
  const storageProvider = store.project.makeScopedStorageProvider();
  try {
    const prefabData = await storageProvider.downloadFileAsJson<ENGINE.SerializedData>(AssetPath.fromString(prefabName));
    console.log(
      '[PrefabActions:ensurePrefabAsset] Downloaded prefab asset',
      prefabName,
      typeof prefabData === 'object' && prefabData != null ? `keys: ${Object.keys(prefabData as object).join(', ')}` : String(prefabData)
    );
    assetsStore.updatePrefabData(prefabName, prefabData);
    if (invalidateCache) {
      handlePrefabAssetExternalReload(prefabName);
    }
    return true;
  } catch (error) {
    console.error('[PrefabActions:ensurePrefabAsset] Error downloading prefab asset', prefabName, error);
    return false;
  }
}

/**
 * Loads a prefab actor asset for prefab mode (non world scene).
 * This assumes the prefab asset is already loaded and cached, if not then use ensurePrefabAsset first.
 * @param prefabName The name of the prefab asset to load.
 * @returns The prefab template actor, or null if not found.
 */
export function loadPrefabAsset(prefabName: string, warnIfNotFound: boolean = false): ENGINE.Actor | null {
  if (!prefabName) {
    if (warnIfNotFound) console.warn('[PrefabActions:loadPrefabAsset] No prefab name provided', prefabName);
    return null;
  }
  // TODO: <AY> Cache prefab template actors?
  const prefabTemplateActor = ENGINE.WorldSerializer.loadPrefabTemplateActor(
    prefabName,
    editorAssets.prefabDataCopy,
    true
  );
  if (!prefabTemplateActor) {
    if (warnIfNotFound) console.warn('[PrefabActions:loadPrefabAsset] Currently only actors are supported as prefabs', prefabName);
    return null;
  }
  (prefabTemplateActor.editorData as EditorObjectData).prefabAsset = prefabName;
  if (syncPrefabActorNameWithAssetPath(prefabTemplateActor, prefabName)) {
    prefabNamesPendingNameSyncSave.add(prefabName);
  }
  return prefabTemplateActor;
}

export async function asyncLoadPrefabAsset(prefabName: string, warnIfNotFound: boolean = false): Promise<ENGINE.Actor | null> {
  if (!prefabName) {
    if (warnIfNotFound) console.warn('[PrefabActions:loadPrefabAsset] No prefab name provided', prefabName);
    return null;
  }
  const prefabTemplateActor = await ENGINE.loadPrefabTemplateInstanceAsync(prefabName, { withoutEditor: false });
  if (!prefabTemplateActor) {
    if (warnIfNotFound) console.warn('[PrefabActions:loadPrefabAsset] Currently only actors are supported as prefabs', prefabName);
    return null;
  }
  (prefabTemplateActor.editorData as EditorObjectData).prefabAsset = prefabName;
  if (syncPrefabActorNameWithAssetPath(prefabTemplateActor, prefabName)) {
    prefabNamesPendingNameSyncSave.add(prefabName);
  }
  return prefabTemplateActor;
}

export function collectPrefabInstancesSnapshots(prefabName: string): Record<string, ENGINE.SerializedData> {
  const snapshots: Record<string, ENGINE.SerializedData> = {};
  const prefabActors = sceneStore.world?.getActorsByPredicate(actor => actor.getPrefabName() === prefabName);
  prefabActors?.forEach(actor => {
    snapshots[actor.uuid] = actor.asExportedObject() as ENGINE.SerializedData;
  });
  return snapshots;
}

/**
 * Reloads all world scene instances of a prefab. Called when we modify a prefab asset and want to keep world instances in sync.
 * @param prefabName The name of the prefab to reload.
 * @param snapshots Optional per-actor export snapshots captured before the prefab asset changed (preserves instance overrides).
 * @param excludeActorUuid When set, that actor is not reloaded (e.g. source instance for apply-to-prefab).
 */
export function reloadPrefabInstances(
  prefabName: string,
  snapshots: Record<string, ENGINE.SerializedData> | null = null,
  excludeActorUuid?: string,
) {
  const prefabActors = sceneStore.world?.getActorsByPredicate(actor => actor.getPrefabName() === prefabName);
  const toReload = excludeActorByUuid(prefabActors, excludeActorUuid);
  toReload.forEach(actor => {
    sceneActions.reloadActorDeferred(actor, snapshots?.[actor.uuid]);
  });
}

/**
 * Browse a prefab asset in the asset browser.
 * @param prefabName The name of the prefab asset to browse.
 */
export function browsePrefab(prefabName: string) {
  showAssetInAssetBrowser(prefabName);
}

/**
 * Open a prefab in prefab mode (non world scene).
 */
export function openPrefab(prefabName: string) {
  console.log('[PrefabActions] Open prefab', prefabName);
  sceneStore.setPrefabEditPath(prefabName);
  // Switch to the prefab's own stack (history preserved across close/reopen).
  syncActiveUndoStack();
  sceneStore.setPrefabReturnActorId(sceneStore.selectedObject?.id ?? null);
  sceneStore.saveCameraState('', true);
  sceneStore.selectObject(prefabName);
}

/**
 * Close a prefab in prefab mode (non world scene), optionally selecting the actor.
 */
export function closePrefab(nextActorIdToSelect: string | null | undefined = undefined) {

  console.log('[PrefabActions] Close prefab', nextActorIdToSelect ? `nextActorIdToSelect: ${nextActorIdToSelect}` : '');
  clearPrefabEditDirty(sceneStore.prefabEditPath ?? undefined);
  sceneStore.setPrefabEditPath(null);
  // Switch back to the scene stack (prefab history preserved for future reopen).
  syncActiveUndoStack();
  const resolvedActorId = sceneStore.resolvePrefabReturnActorId(nextActorIdToSelect);
  if (resolvedActorId) {
    const actor = sceneActions.selectActorById(resolvedActorId);
    if (actor) {
      // sceneActions.focusSelectedObjects();
      return;
    }
  }
  sceneStore.selectObject(null);
}

/**
 * Applies changes from a prefab instance back to the prefab asset.
 * @param object The editable object or scene tree node representing the prefab instance
 */
export async function applyToPrefabAsset(object: IEditableObject | ISceneTreeNode): Promise<boolean> {
  const actor = object?.asActor;
  if (!actor) {
    toast.error(`Failed to apply to prefab: No actor found for ${object.name}`);
    return false;
  }

  const prefabName = actor.getPrefabName();
  if (!prefabName) {
    toast.error(`Failed to apply to prefab: ${object.name} is not a prefab instance`);
    return false;
  }

  const storageProvider = store.project.makeScopedStorageProvider();
  try {
    const instancesSnapshots = collectPrefabInstancesSnapshots(prefabName);
    const prefabData = exportPrefabDataWithoutRootLocalTransform(actor, true);

    const prefabJson = JSON.stringify(prefabData, null, 2);
    const result = await storageProvider.uploadFile(AssetPath.fromString(prefabName), new Blob([prefabJson], { type: 'application/json' }));

    ENGINE.PrefabManager.setPrefabData(prefabName, undefined);
    ENGINE.resourceManager.invalidateJsonObjectCache(prefabName);
    assetsStore.updatePrefabData(prefabName, prefabData);

    editorActions.notifyChanged('applyToPrefabAsset', { path: result.path });
    toast.info(`Applied changes to prefab: ${prefabName}`, {
      description: 'Instance changes saved to prefab asset'
    });

    reloadPrefabInstances(prefabName, instancesSnapshots, actor.uuid);

    // Update asset file state
    const assetFile = assetsStore.assetTree.files.get(prefabName);
    assetFile?.setDirty();

    // Request thumbnail update
    const thumbnailService = editorStore.thumbnailService;
    if (thumbnailService && assetFile?.supportsThumbnails) {
      thumbnailService.queueThumbnailGeneration(assetFile, 100, true);
    }

    await autoSaveSceneAfterPrefabMutation('applyToPrefabAsset');
    console.log(`🔄 Applied instance changes to prefab: ${prefabName}`);
    return true;
  } catch (error) {
    toast.error(`Failed to apply changes to prefab: ${prefabName}`);
    console.error('[PrefabActions:applyToPrefabAsset] Error applying changes to prefab asset', editableObjectOrNodeRef(object), error);
    return false;
  }
}

/**
 * Reverts a prefab instance to match the current prefab asset, discarding local overrides.
 * @param object The editable object or scene tree node representing the prefab instance
 */
export async function resyncPrefabInstance(object: IEditableObject | ISceneTreeNode) {
  const actor = object?.asActor;
  if (!actor) {
    toast.error(`Failed to resync prefab: No actor found for ${object.name}`);
    return;
  }

  const prefabName = actor.getPrefabName();
  if (!prefabName) {
    toast.error(`Failed to resync prefab: ${object.name} is not a prefab instance`);
    return;
  }

  let freshActor: ENGINE.Actor | null = null;
  try {
    const loaded = await ensurePrefabAsset(prefabName, true);
    if (!loaded) {
      toast.error(`Failed to resync prefab: Could not load prefab asset ${prefabName}`);
      return;
    }

    const uuid = actor.uuid;
    const preservedDisplayName = nameHelpers.getActorDisplayName(actor);
    const position = actor.getWorldPosition().clone();
    const rotation = actor.getWorldRotation().clone();
    const scale = actor.getWorldScale().clone();

    freshActor = ENGINE.instantiatePrefab(prefabName, editorAssets.prefabDataCopy, {
      position,
      rotation,
      scale,
    }) as ENGINE.Actor | null;

    if (!freshActor) {
      toast.error(`Failed to resync prefab: Could not instantiate ${prefabName}`);
      return;
    }

    const freshUuid = freshActor.uuid;
    let instanceData = freshActor.asExportedObject() as ENGINE.SerializedData;
    if (freshUuid !== uuid) {
      instanceData = ENGINE.WorldSerializer.remapSerializedUuids(instanceData, { [freshUuid]: uuid });
    }

    const reloaded = await sceneActions.reloadActor(actor, instanceData);
    if (!reloaded) {
      toast.error(`Failed to resync prefab: ${object.name}`);
      return;
    }

    const reloadedActor = sceneStore.world?.getActorByUuid(uuid);
    if (reloadedActor) {
      if (preservedDisplayName && reloadedActor.name !== preservedDisplayName) {
        nameHelpers.setCustomDisplayName(reloadedActor, preservedDisplayName);
      }
      syncActorNameInEditorTrees(reloadedActor);
    }

    toast.success(`Prefab instance resynced: ${object.name}`, {
      description: `Reverted local overrides to match ${getPrefabPathLabel(prefabName)}`,
    });

    await autoSaveSceneAfterPrefabMutation('resyncPrefabInstance');
    console.log(`🔄 Resynced prefab instance: ${object.name} (${prefabName})`);
  } catch (error) {
    toast.error(`Failed to resync prefab: ${object.name}`);
    console.error('[PrefabActions:resyncPrefabInstance] Error resyncing prefab instance', editableObjectOrNodeRef(object), error);
  } finally {
    freshActor?.destroy();
  }
}

/**
 * Unlinks a prefab from an actor prefab instance, converting it to a regular actor.
 * @param object The editable object or scene tree node to unlink from its prefab
 */
export async function unlinkPrefabFromActor(object: IEditableObject | ISceneTreeNode): Promise<boolean> {
  const actor = object?.asActor;
  if (!actor) {
    toast.error(`Failed to unlink prefab: No actor found for ${object.name}`);
    return false;
  }

  const prefabName = actor.getPrefabName();
  if (!prefabName) {
    toast.error(`Failed to unlink prefab: ${object.name} is not a prefab instance`);
    return false;
  }

  try {
    // Clear the prefab association
    ENGINE.setAsPrefabInstance(actor, null);

    // Refresh the outliner to reflect the change
    sceneStore.outliner.getNodeById(actor.uuid)?.init(actor);

    toast.info(`Prefab unlinked from ${object.name}`, {
      description: `Actor is no longer linked to ${prefabName}`
    });

    await autoSaveSceneAfterPrefabMutation('unlinkPrefabFromActor');
    console.log(`🔗 Prefab unlinked from actor: ${object.name} (was: ${prefabName})`);
    return true;

  } catch (error) {
    toast.error(`Failed to unlink prefab from ${object.name}`);
    console.error('[PrefabActions:unlinkPrefabFromActor] Error unlinking prefab from actor', editableObjectOrNodeRef(object), error);
    return false;
  }
}
